import { useEffect, useId, useRef, useState } from "react";
import "./layout.css";

export interface TabItem {
  key: string;
  label: string;
  dataTour?: string;
}

export interface TabsProps {
  items: TabItem[];
  activeKey: string;
  onChange: (key: string) => void;
}

const COMPACT_TABS_QUERY = "(max-width: 64rem)";
const COLLAPSE_AT = 5;

function useCompactTabs(): boolean {
  const [compact, setCompact] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(COMPACT_TABS_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(COMPACT_TABS_QUERY);
    const sync = () => setCompact(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return compact;
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ui-tabs__menu-icon" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Underline-style tab strip. Long strips become the current tab plus a menu on a phone or tablet. */
export function Tabs({ items, activeKey, onChange }: TabsProps) {
  const compact = useCompactTabs();
  const collapse = compact && items.length >= COLLAPSE_AT;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const active = items.find((item) => item.key === activeKey) ?? items[0];

  useEffect(() => {
    if (!open) return;
    function onPointer(event: globalThis.MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (collapse) {
    return (
      <div className="ui-tabs ui-tabs--compact" ref={root}>
        <span className="ui-tabs__current">{active?.label}</span>
        <button
          type="button"
          className="ui-tabs__menu"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label="Open sections menu"
          onClick={() => setOpen((value) => !value)}
        >
          <MenuIcon />
          <span>Menu</span>
        </button>
        {open ? (
          <div id={panelId} className="ui-tabs__panel" role="menu">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                data-tour={item.dataTour}
                className={item.key === activeKey ? "is-active" : undefined}
                onClick={() => {
                  setOpen(false);
                  onChange(item.key);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="ui-tabs">
      <div className="ui-tabs__row">
        {items.map((item) => {
          const activeTab = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              data-tour={item.dataTour}
              onClick={() => onChange(item.key)}
              style={{
                background: "none",
                border: "none",
                padding: "var(--space-3) 0",
                marginBottom: -1,
                fontSize: "var(--font-size-md)",
                fontWeight: activeTab ? "var(--font-weight-bold)" : "var(--font-weight-normal)",
                color: activeTab ? "var(--color-primary)" : "var(--color-text)",
                borderBottom: activeTab ? "2px solid var(--color-primary)" : "2px solid transparent",
                cursor: "pointer",
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
